import { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import InventoryTableRow from "./InventoryTableRow";
import { MoverInventoryItem } from "@/types";
import { Button } from "@/components/ui/button";

export type CustomInventoryItem = {
  id: number | null;
  mover_inventory_item_id: null;
  uuid: string;
  name: string;
  description: null;
  room_or_type: "Custom";
  volume_cuft: number;
  weight_lbs: number;
  quantity: number;
  total_weight_lbs: number;
  total_volume_cuft: number;
};

export type InventoryItemForm = {
  id: number | null;
  mover_inventory_item_id: number | null;
  uuid: string;
  name: string;
  description: string | null;
  room_or_type: string | null;
  volume_cuft: number;
  weight_lbs: number | null;
  quantity: number;
  total_weight_lbs: number;
  total_volume_cuft: number;
};

type InventoryTableProps = {
  moverInventoryItems: MoverInventoryItem[];
  items: InventoryItemForm[];
  updateCustomItem: (
    uuid: string,
    field: keyof CustomInventoryItem,
    value: string | number
  ) => void;
  onQuantityChange: (item: InventoryItemForm, quantity: number) => void;
  onDeleteItem?: (uuid: string) => void;
  getItemQuantity: (uuid: string) => number;
  category: string | null;
  onCategoryChange: (category: string | null) => void;
  onAddCustomItem: () => void;
  onSearchChange: (search: string) => void;
  customerFacing?: boolean;
};

const humanizeCategory = (category: string | null): string => {
  if (!category) return "Uncategorized";

  // Replace underscores with spaces and capitalize each word
  return category
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const InventoryTable = ({
  moverInventoryItems,
  items,
  updateCustomItem,
  onQuantityChange,
  onDeleteItem,
  getItemQuantity,
  category,
  onCategoryChange,
  onAddCustomItem,
  onSearchChange,
  customerFacing = false,
}: InventoryTableProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    category || null
  );

  useEffect(() => {
    onCategoryChange(selectedCategory);
  }, [selectedCategory, onCategoryChange]);

  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          moverInventoryItems.map(
            (item) => item.room_or_type || "Uncategorized"
          )
        )
      ).sort(),
    [moverInventoryItems]
  );

  return (
    <div className="mt-4 flex flex-col lg:flex-row h-full gap-4">
      <div className="w-full md:w-[140px] flex flex-row md:flex-col flex-wrap pr-4 pr-0 md:pr-4 overflow-y-auto md:border-r gap-2 md:gap-0">
        <div
          className={cn(
            "px-2 py-0.5 cursor-pointer hover:bg-gray-100",
            !selectedCategory && "bg-amber-100 font-medium"
          )}
          onClick={() => setSelectedCategory(null)}
        >
          All
        </div>

        {categories.map((category) => (
          <div
            key={category}
            className={cn(
              "px-2 py-0.5 cursor-pointer hover:bg-gray-100",
              selectedCategory === category && "bg-amber-100 font-medium"
            )}
            onClick={() => setSelectedCategory(category)}
          >
            {humanizeCategory(category)}
          </div>
        ))}
      </div>
      <div className="w-full lg:w-4/5 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {!selectedCategory && <TableHead>Category</TableHead>}
              <TableHead>Name</TableHead>
              {!customerFacing && <TableHead>Vol.</TableHead>}
              {!customerFacing && <TableHead>Wt.</TableHead>}
              <TableHead>Quantity</TableHead>
              {/* <TableHead>Total Vol (cuft)/Wt (lbs)</TableHead> */}
              {!customerFacing && onDeleteItem && (
                <TableHead className="w-24"></TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {!customerFacing && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Button
                    variant="link"
                    className="pl-0 text-sm text-blue-600 hover:text-blue-800"
                    onClick={() => {
                      onAddCustomItem();
                      onSearchChange("");
                      setSelectedCategory(null);
                    }}
                  >
                    Add Custom Item
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {items.map((item) => (
              <InventoryTableRow
                key={item.uuid}
                item={item}
                selectedCategory={selectedCategory}
                updateCustomItem={updateCustomItem}
                onQuantityChange={onQuantityChange}
                onDeleteItem={onDeleteItem}
                getItemQuantity={getItemQuantity}
                customerFacing={customerFacing}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default InventoryTable;
